import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ExternalLink, Loader2, X } from "lucide-react";
import {
  IntellioInsightCharts,
  type IntellioChartPanel,
} from "@triedge/ai-assistant-ui";
import type { AdmissionsAssistantArtifactWorkspaceRef } from "./types";
import {
  resolveAssistantChartArtifactState,
  type AssistantChartArtifactStateResolver,
} from "./assistantChartArtifactState";
import { SupportErrorMessage } from "../overlays/EngineeringSupportBanner";

type AssistantChartWorkspaceProps = {
  chartArtifact: AdmissionsAssistantArtifactWorkspaceRef;
  initialCharts?: IntellioChartPanel[];
  onClose: () => void;
  presentation?: "portal" | "embedded";
  resolveChartArtifactState?: AssistantChartArtifactStateResolver;
};

const buildDownloadStem = (value: string): string =>
  value
    .trim()
    .replace(/\.[a-z0-9]+$/i, "")
    .replace(/[^a-z0-9]+/gi, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase() || "assistant-chart";

const escapeCsvCell = (value: unknown): string => {
  const normalized = String(value ?? "");
  return /[",\n]/.test(normalized)
    ? `"${normalized.replace(/"/g, '""')}"`
    : normalized;
};

const buildChartCsv = (panel: IntellioChartPanel): string => {
  const seriesColumns = panel.series.map((entry) => entry.key);
  const rows = [
    [panel.xKey, ...seriesColumns].map(escapeCsvCell).join(","),
    ...panel.data.map((row) =>
      [row[panel.xKey], ...seriesColumns.map((seriesKey) => row[seriesKey])]
        .map(escapeCsvCell)
        .join(","),
    ),
  ];
  return rows.join("\n");
};

export const AssistantChartWorkspace: React.FC<
  AssistantChartWorkspaceProps
> = ({
  chartArtifact,
  initialCharts = [],
  onClose,
  presentation = "portal",
  resolveChartArtifactState,
}) => {
  const [charts, setCharts] = useState<IntellioChartPanel[]>(initialCharts);
  const [resolvedChartArtifact, setResolvedChartArtifact] =
    useState(chartArtifact);
  const [isLoading, setIsLoading] = useState<boolean>(
    initialCharts.length === 0,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const chartArtifactRefreshKey =
    chartArtifact.sourceRef ?? chartArtifact.downloadUrl;
  const resolveChartArtifactStateRef = useRef(resolveChartArtifactState);
  const chartCardRefs = useRef<Record<string, HTMLElement | null>>({});
  resolveChartArtifactStateRef.current = resolveChartArtifactState;

  useEffect(() => {
    let cancelled = false;
    setResolvedChartArtifact(chartArtifact);
    setCharts(initialCharts);
    setIsLoading(initialCharts.length === 0);
    setErrorMessage(null);
    void (async () => {
      try {
        const resolvedState = await resolveAssistantChartArtifactState({
          chartArtifact,
          initialCharts,
          refreshChartArtifactState: resolveChartArtifactStateRef.current,
          debugSource: "chart-workspace",
        });
        if (cancelled) {
          return;
        }
        setResolvedChartArtifact(resolvedState.chartArtifact);
        setCharts(resolvedState.chartPanels);
        setErrorMessage(
          resolvedState.chartPanels.length > 0
            ? null
            : "Chart artifact payload is invalid.",
        );
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Unable to load chart workspace.",
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [chartArtifactRefreshKey, initialCharts.length]);

  const downloadBlob = useCallback((blob: Blob, fileName: string) => {
    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => {
      window.URL.revokeObjectURL(objectUrl);
    }, 0);
  }, []);

  const handleExportCsv = useCallback(
    (panel: IntellioChartPanel) => {
      const csv = buildChartCsv(panel);
      downloadBlob(
        new Blob([csv], { type: "text/csv;charset=utf-8" }),
        `${buildDownloadStem(panel.title || resolvedChartArtifact.label)}.csv`,
      );
    },
    [downloadBlob, resolvedChartArtifact.label],
  );

  const handleExportPng = useCallback(
    async (panel: IntellioChartPanel) => {
      const cardNode = chartCardRefs.current[panel.id];
      const chartSurface =
        cardNode?.querySelector("svg.recharts-surface") ??
        cardNode?.querySelector("svg");
      if (!(chartSurface instanceof SVGSVGElement)) {
        throw new Error("Chart surface is not ready for export.");
      }

      const serializer = new XMLSerializer();
      const clonedSvg = chartSurface.cloneNode(true) as SVGSVGElement;
      clonedSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      clonedSvg.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");

      const { width: measuredWidth, height: measuredHeight } =
        chartSurface.getBoundingClientRect();
      const viewBox = clonedSvg.viewBox.baseVal;
      const exportWidth =
        Math.round(measuredWidth) ||
        Math.round(
          viewBox?.width || Number(clonedSvg.getAttribute("width")) || 960,
        );
      const exportHeight =
        Math.round(measuredHeight) ||
        Math.round(
          viewBox?.height || Number(clonedSvg.getAttribute("height")) || 540,
        );
      clonedSvg.setAttribute("width", String(exportWidth));
      clonedSvg.setAttribute("height", String(exportHeight));

      const svgMarkup = serializer.serializeToString(clonedSvg);
      const svgBlob = new Blob([svgMarkup], {
        type: "image/svg+xml;charset=utf-8",
      });
      const svgUrl = window.URL.createObjectURL(svgBlob);

      await new Promise<void>((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = exportWidth;
          canvas.height = exportHeight;
          const context = canvas.getContext("2d");
          if (!context) {
            window.URL.revokeObjectURL(svgUrl);
            reject(new Error("PNG export is unavailable in this browser."));
            return;
          }
          context.fillStyle = "#ffffff";
          context.fillRect(0, 0, exportWidth, exportHeight);
          context.drawImage(image, 0, 0, exportWidth, exportHeight);
          canvas.toBlob((blob) => {
            window.URL.revokeObjectURL(svgUrl);
            if (!blob) {
              reject(new Error("PNG export could not generate a file."));
              return;
            }
            downloadBlob(
              blob,
              `${buildDownloadStem(panel.title || resolvedChartArtifact.label)}.png`,
            );
            resolve();
          }, "image/png");
        };
        image.onerror = () => {
          window.URL.revokeObjectURL(svgUrl);
          reject(new Error("PNG export failed to render the chart."));
        };
        image.src = svgUrl;
      });
    },
    [downloadBlob, resolvedChartArtifact.label],
  );

  if (presentation === "portal" && typeof document === "undefined") {
    return null;
  }

  const headline = charts[0]?.title ?? resolvedChartArtifact.label;
  const subtitle =
    charts[0]?.subtitle ??
    "Review the assistant-generated chart in a left-side workspace while the assistant remains live on the right.";
  const isEmbeddedWorkspace = presentation === "embedded";
  const embeddedWorkspaceBody = (
    <div className="min-h-0 flex-1 overflow-auto bg-[#F8FAF9] px-4 py-4 lg:px-5">
      <div className="mb-3 flex justify-end">
        <button
          type="button"
          className="rounded-full border border-[#D8EAE7] bg-white p-2 text-irongrey/60 transition-colors hover:border-primary/20 hover:text-primary"
          onClick={onClose}
          aria-label="Close chart workspace"
          data-testid="assistant-chart-workspace-close"
        >
          <X size={16} />
        </button>
      </div>
      {isLoading ? (
        <div className="flex h-full items-center justify-center text-sm text-irongrey/70">
          <Loader2 size={16} className="mr-2 animate-spin" />
          Loading chart workspace…
        </div>
      ) : errorMessage ? (
        <SupportErrorMessage>
          {errorMessage}
        </SupportErrorMessage>
      ) : charts.length > 0 ? (
        <div
          className="space-y-4"
          data-testid="assistant-chart-workspace-cards"
        >
          {charts.map((panel) => (
            <section
              key={panel.id}
              ref={(node) => {
                chartCardRefs.current[panel.id] = node;
              }}
              className="rounded-[22px] border border-[#D8EAE7] bg-white p-5 shadow-[0_0px_3px_2px_rgba(206,242,237,0.39)]"
              data-testid={`assistant-chart-workspace-card-${panel.id}`}
            >
              <IntellioInsightCharts
                charts={[panel]}
                panelHeight={320}
                layoutMode="comfortable"
                maxPanelWidth={1040}
                panelChrome="bare"
              />
              <div className="mt-4 flex flex-wrap items-center gap-4 text-[15px]">
                <button
                  type="button"
                  className="font-medium text-[#0891A8] transition-colors hover:text-[#0b7285]"
                  onClick={() => {
                    void handleExportPng(panel).catch((error) => {
                      console.warn(
                        "[AssistantChartWorkspace] PNG export failed",
                        error,
                      );
                    });
                  }}
                  data-testid={`assistant-chart-export-png-${panel.id}`}
                >
                  Export as PNG
                </button>
                <button
                  type="button"
                  className="font-medium text-[#0891A8] transition-colors hover:text-[#0b7285]"
                  onClick={() => {
                    handleExportCsv(panel);
                  }}
                  data-testid={`assistant-chart-export-csv-${panel.id}`}
                >
                  Export as CSV
                </button>
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 bg-white p-4 text-sm text-irongrey/70">
          No chart panels are available.
        </div>
      )}
    </div>
  );

  const portalWorkspaceBody = (
    <>
      <header className="border-b border-gray-200 bg-[linear-gradient(135deg,rgba(246,250,248,0.98),rgba(255,255,255,0.98))] px-6 py-5 lg:px-8 lg:py-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary/70">
              Chart workspace
            </p>
            <h2 className="mt-1 truncate text-xl font-black text-slate-900">
              {headline}
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-irongrey/80">
              {subtitle}
            </p>
          </div>
          <button
            type="button"
            className="rounded-full border border-gray-200 bg-white p-2 text-irongrey/60 hover:border-primary/20 hover:text-primary"
            onClick={onClose}
            aria-label="Close chart workspace"
          >
            <X size={16} />
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <a
            href={resolvedChartArtifact.downloadUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-white px-4 py-2 text-[12px] font-semibold text-primary hover:bg-primary/5"
          >
            Open saved artifact
            <ExternalLink size={14} />
          </a>
        </div>
        <p className="mt-3 text-[12px] text-irongrey/72">
          Keep the assistant open while you inspect this chart. You can ask for
          regeneration, compare another cut, or follow up on what you see.
        </p>
      </header>

      <div className="min-h-0 flex-1 overflow-auto bg-[linear-gradient(180deg,rgba(249,250,251,0.9),rgba(255,255,255,1))] px-5 py-5 lg:px-7 lg:py-6">
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-irongrey/70">
            <Loader2 size={16} className="mr-2 animate-spin" />
            Loading chart workspace…
          </div>
        ) : errorMessage ? (
          <SupportErrorMessage>
            {errorMessage}
          </SupportErrorMessage>
        ) : charts.length > 0 ? (
          <IntellioInsightCharts
            charts={charts}
            panelHeight={320}
            layoutMode="comfortable"
            maxPanelWidth={1040}
          />
        ) : (
          <div className="rounded-2xl border border-gray-200 bg-white p-4 text-sm text-irongrey/70">
            No chart panels are available.
          </div>
        )}
      </div>
    </>
  );

  const workspaceShell = (
    <div
      className={
        isEmbeddedWorkspace
          ? "flex h-full min-h-0 flex-col overflow-hidden bg-[#F8FAF9]"
          : "pointer-events-auto absolute inset-y-3 left-3 right-3 overflow-hidden rounded-[30px] border border-white/70 bg-white shadow-[0_30px_120px_rgba(18,38,41,0.28)] md:right-[22rem] lg:left-4 lg:right-[27rem] xl:right-[30rem] 2xl:right-[32rem]"
      }
      data-testid="assistant-chart-workspace-shell"
    >
      <div className="flex h-full flex-col">
        {isEmbeddedWorkspace ? embeddedWorkspaceBody : portalWorkspaceBody}
      </div>
    </div>
  );

  if (presentation === "embedded") {
    return (
      <div
        className="flex h-full min-h-0 flex-col"
        data-testid="assistant-chart-workspace"
      >
        {workspaceShell}
      </div>
    );
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[320] pointer-events-none"
      data-testid="assistant-chart-workspace"
    >
      <div className="absolute inset-0 bg-slate-900/22 backdrop-blur-[2px]" />
      {workspaceShell}
    </div>,
    document.body,
  );
};
